<template>
  <loading-element v-if="authStore.isLoading"></loading-element>

  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5 pageHeader">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Liste de"
        secondElement="courses"
        coloredSecond="true"
      ></TopDeck>
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <div v-if="!hasLists" class="emptyStateCard">
      <No_elements_create_new
        title="Aucune liste de courses"
        description="Vous n'avez pas encore créé de liste de courses."
        :imageSrc="shoppingSvg"
        buttonText="Créer une nouvelle liste"
        :buttonAction="openCreateModal"
      />
    </div>

    <div v-else class="shoppingLayout">
      <div class="listColumn">
        <div class="listHeader">
          <div>
            <h2 class="listTitle">Mes listes</h2>
            <p class="listSubtitle">Retrouvez et gérez vos courses en un clic.</p>
          </div>
          <v-btn color="accent" class="primaryBtn" @click="openCreateModal">
            <v-icon class="mr-1">mdi-plus</v-icon>
            Nouvelle liste
          </v-btn>
        </div>

        <div class="listGrid">
          <button
            v-for="list in shoppingListArray"
            :key="list.id"
            class="listCard"
            :class="{ active: list.id === selectedListId }"
            @click="selectList(list.id)"
          >
            <div class="listCardHeader">
              <div>
                <h3 class="listCardTitle">{{ list.name }}</h3>
                <p class="listCardMeta">{{ list.items?.length || 0 }} articles</p>
              </div>
              <span class="listCardDate">{{ formatDate(list.updatedAt || list.createdAt) }}</span>
            </div>
            <div class="listPreview">
              <div
                v-for="(item, idx) in (list.items || []).slice(0, 3)"
                :key="`${list.id}-preview-${idx}`"
                class="listPreviewRow"
              >
                <span>{{ item.name }}</span>
                <span class="listPreviewQty">x{{ item.quantity }}</span>
              </div>
              <div v-if="(list.items || []).length > 3" class="listPreviewMore">
                +{{ (list.items || []).length - 3 }} autres articles
              </div>
            </div>
          </button>
        </div>
      </div>

      <div class="detailPanel" v-if="selectedList">
        <div class="panelHeader">
          <div>
            <h3 class="panelTitle">{{ selectedList.name }}</h3>
            <p class="panelMeta">{{ selectedList.items?.length || 0 }} articles</p>
          </div>
          <div class="panelActions">
            <v-btn icon size="small" variant="text" @click="openEditModal(selectedList)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon size="small" variant="text" color="error" @click="removeList(selectedList.id)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-btn icon size="small" variant="text" @click="printList(selectedList)">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="panelBody">
          <div v-if="!selectedList.items?.length" class="panelEmpty">
            Aucun article dans cette liste.
          </div>
          <div v-else class="panelItems">
            <div
              v-for="(item, idx) in selectedList.items"
              :key="`${selectedList.id}-item-${idx}`"
              class="panelItem"
              :class="{ 'panelItem--checked': item.checked }"
              @click="toggleItemChecked(selectedList.id, idx)"
            >
              <v-icon size="16" class="panelItem__check">{{ item.checked ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
              <span class="panelItemName">{{ item.name }}</span>
              <span class="panelItemQty" v-if="item.grams">{{ item.grams }}g</span>
              <span class="panelItemQty" v-else>x{{ item.quantity }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <v-snackbar
    v-if="errorMessage"
    v-model="errorMessage"
    color="error"
    top
    right
  >
    {{ errorMessage }}
  </v-snackbar>

  <!-- Modal pour ajouter des éléments dans une liste de course -->
  <v-dialog v-model="showDialog" max-width="680px">
    <v-card class="shoppingModal">
        <v-card-title class="modalHeader">
          <div>
            <div class="modalTitle">Créer une liste de courses</div>
            <div class="modalSubtitle">Ajoutez vos produits puis créez la liste.</div>
          </div>
          <v-btn icon variant="text" class="modalClose" @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
      <v-card-text class="modalBody">
        <v-text-field
          v-model="listName"
          label="Nom de la liste"
          variant="outlined"
          density="comfortable"
          class="listNameField"
        />

        <div class="productList">
          <div v-if="!products.length" class="emptyProducts">
            Aucun produit ajouté pour le moment.
          </div>
          <div
            v-for="(item, index) in products"
            :key="index"
            class="productRow"
          >
            <div class="productMeta">
              <span class="productName">{{ item.name }}</span>
              <span class="productQty">x{{ item.quantity }}</span>
            </div>

            <div class="productActions">
              <v-btn icon size="small" variant="text" @click="editProduct(index)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

              <v-btn
                icon
                size="small"
                variant="text"
                color="error"
                @click="removeProduct(index)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Barre d'ajout en bas -->
        <div class="addRow">
          <v-text-field
            width="100%"
            v-model="currentProduct.name"
            label="Produit"
            variant="outlined"
            density="comfortable"
            class="productField"
            hide-details
          />
          <v-text-field
            v-model="currentProduct.quantity"
            label="Qté (ex: 2 ou 80 g)"
            type="text"
            inputmode="decimal"
            variant="outlined"
            density="comfortable"
            class="qtyField"
            hide-details
          />

          <v-btn
            @click="isEditing ? updateProduct() : addProduct()"
            color="accent"
            class="addProductBtn"
          >
            {{ isEditing ? "Modifier" : "Ajouter" }}
          </v-btn>
        </div>
      </v-card-text>
      <v-card-actions class="modalActions">
        <v-spacer />
        <v-btn @click="showDialog = false" variant="text" class="ghostBtn">Annuler</v-btn>
      <v-btn @click="createShoppingList" color="accent" variant="flat" class="primaryBtn">
        {{ isEditingList ? "Mettre à jour" : "Créer" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
</template>

<script>
import { ref, computed, watch } from "vue";
import LoadingElement from "../Animations/LoadingElement.vue";
import TopDeck from "../Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import No_elements_create_new from "../Utils/NoElementsDisplayCreate.vue";
import shoppingSvg from "@/assets/SvgIcons/shopping.svg";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";
import {
  addShoppingList,
  getUserShoppingLists,
  updateShoppingList,
  deleteShoppingList,
} from "@/service/shopping_list_service";
import { getUserWeeklyMenu } from "@/service/menu_storage_service";

export default {
  name: "ShoppingList",
  components: {
    LoadingElement,
    TopDeck,
    No_elements_create_new,
    ThemeToggle,
    AccountToggle,
  },
  setup() {
    const authStore = useAuthStore();
    const errorMessage = ref("");
    const showDialog = ref(false);
    const products = ref([]);
    const currentProduct = ref({ name: "", quantity: "1" });
    const isEditing = ref(false);
    const editingIndex = ref(null);
    const listName = ref("");
    const listsById = ref({});
    const selectedListId = ref(null);
    const editingListId = ref(null);

    const userId = computed(() => authStore?.userid || authStore?.userInfos?.userid);

    const shoppingListArray = computed(() =>
      Object.entries(listsById.value || {})
        .map(([id, list]) => ({ id, ...list }))
        .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0))
    );

    const hasLists = computed(() => shoppingListArray.value.length > 0);

    const selectedList = computed(() =>
      shoppingListArray.value.find((list) => list.id === selectedListId.value) || null
    );

    const isEditingList = computed(() => Boolean(editingListId.value));

    const normalizeQuantity = (value) => {
      const raw = String(value ?? "").trim();
      if (!raw) return 1;
      const numeric = Number(raw);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
      return raw;
    };

    const normalizeItems = (items) =>
      (items || [])
        .filter((item) => item?.name && item.name.trim())
        .map((item) => ({
          name: item.name.trim(),
          quantity: normalizeQuantity(item.quantity),
        }));

    const normalizeListName = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

    const addProduct = () => {
      const name = String(currentProduct.value.name || "").trim();
      if (!name) return;
      const qty = String(currentProduct.value.quantity ?? "").trim();
      products.value.push({ name, quantity: qty || "1" });
      currentProduct.value = { name: "", quantity: "1" };
    };

    const editProduct = (index) => {
      currentProduct.value = {
        ...products.value[index],
        quantity: String(products.value[index]?.quantity ?? "1"),
      };
      editingIndex.value = index;
      isEditing.value = true;
    };

    const updateProduct = () => {
      if (editingIndex.value !== null) {
        const name = String(currentProduct.value.name || "").trim();
        if (!name) return;
        const qty = String(currentProduct.value.quantity ?? "").trim();
        products.value[editingIndex.value] = { name, quantity: qty || "1" };
        isEditing.value = false;
        editingIndex.value = null;
        currentProduct.value = { name: "", quantity: "1" };
      }
    };

    const removeProduct = (index) => {
      products.value.splice(index, 1);
    };

    const resetForm = () => {
      listName.value = "";
      products.value = [];
      currentProduct.value = { name: "", quantity: "1" };
      isEditing.value = false;
      editingIndex.value = null;
      editingListId.value = null;
    };

    const openCreateModal = () => {
      resetForm();
      showDialog.value = true;
    };

    const openEditModal = (list) => {
      if (!list) return;
      listName.value = list.name || "";
      products.value = Array.isArray(list.items) ? list.items.map((i) => ({ ...i })) : [];
      currentProduct.value = { name: "", quantity: "1" };
      isEditing.value = false;
      editingIndex.value = null;
      editingListId.value = list.id;
      showDialog.value = true;
    };

    const selectList = (listId) => {
      selectedListId.value = listId;
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return "—";
      try {
        return new Date(timestamp).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch {
        return "—";
      }
    };

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const isGeneratingFromMenu = ref(false);

    const generateShoppingListFromMenu = async () => {
      if (!userId.value) return;
      isGeneratingFromMenu.value = true;
      try {
        const saved = await getUserWeeklyMenu(userId.value);
        if (!saved || !saved.weeklyMenu || !saved.weeklyMenu.daily) {
          errorMessage.value = "Aucun menu sauvegardé. Générez d'abord un menu.";
          return;
        }
        const daily = saved.weeklyMenu.daily;

        // Extract all ingredients from all recipes in the weekly menu
        const ingredientsMap = {};
        for (const day of daily) {
          if (!day.menu) continue;
          for (const meals of Object.values(day.menu)) {
            for (const recipe of meals) {
              if (!recipe.ingredients) continue;
              for (const ing of recipe.ingredients) {
                const name = ing.name || ing.productName || '';
                if (!name) continue;
                const key = name.toLowerCase().trim();
                if (ingredientsMap[key]) {
                  ingredientsMap[key].grams = (ingredientsMap[key].grams || 0) + (ing.grams || 0);
                } else {
                  ingredientsMap[key] = { name, grams: ing.grams || 0, quantity: '1', checked: false };
                }
              }
            }
          }
        }

        const items = Object.values(ingredientsMap);
        if (!items.length) {
          errorMessage.value = "Aucun ingrédient trouvé dans le menu.";
          return;
        }

        const listNameGenerated = "Semaine du " + new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
        const { id } = await addShoppingList(userId.value, {
          name: listNameGenerated,
          items,
          userGenerated: false,
          generatedFromMenu: true,
        });
        listsById.value = {
          ...listsById.value,
          [id]: { id, name: listNameGenerated, items, userGenerated: false, generatedFromMenu: true, createdAt: Date.now(), updatedAt: Date.now() },
        };
        selectedListId.value = id;
      } catch (e) {
        console.error(e);
        errorMessage.value = "Erreur lors de la génération de la liste.";
      } finally {
        isGeneratingFromMenu.value = false;
      }
    };

    const loadLists = async () => {
      if (!userId.value) return;
      try {
        const data = await getUserShoppingLists(userId.value);
        listsById.value = data || {};
      } catch (e) {
        errorMessage.value = "Erreur lors du chargement des listes.";
      }
    };

    const createShoppingList = async () => {
      if (!userId.value) {
        errorMessage.value = "Utilisateur non authentifié.";
        return;
      }
      const cleanedItems = normalizeItems(products.value);
      const trimmedName = listName.value.trim();
      if (!trimmedName) {
        errorMessage.value = "Veuillez saisir un nom de liste.";
        return;
      }
      const targetName = normalizeListName(trimmedName);
      const duplicate = Object.entries(listsById.value || {}).find(([id, list]) => {
        if (editingListId.value && id === editingListId.value) return false;
        return normalizeListName(list?.name) === targetName;
      });
      if (duplicate) {
        errorMessage.value = "Une liste avec ce nom existe déjà.";
        return;
      }
      try {
        if (editingListId.value) {
          const existing = listsById.value[editingListId.value] || {};
          await updateShoppingList(userId.value, editingListId.value, {
            name: trimmedName,
            items: cleanedItems,
            userGenerated: existing.userGenerated ?? true,
            createdAt: existing.createdAt,
          });
          listsById.value = {
            ...listsById.value,
            [editingListId.value]: {
              ...existing,
              id: editingListId.value,
              name: trimmedName,
              items: cleanedItems,
              userGenerated: existing.userGenerated ?? true,
              updatedAt: Date.now(),
            },
          };
        } else {
          const { id } = await addShoppingList(userId.value, {
            name: trimmedName,
            items: cleanedItems,
            userGenerated: true,
          });
          listsById.value = {
            ...listsById.value,
            [id]: {
              id,
              name: trimmedName,
              items: cleanedItems,
              userGenerated: true,
              createdAt: Date.now(),
              updatedAt: Date.now(),
            },
          };
        }
        showDialog.value = false;
        resetForm();
      } catch (e) {
        errorMessage.value = "Erreur lors de l'enregistrement de la liste.";
      }
    };

    const toggleItemChecked = async (listId, itemIdx) => {
      if (!userId.value || !listId) return;
      const list = listsById.value[listId];
      if (!list) return;
      const items = [...(list.items || [])];
      items[itemIdx] = { ...items[itemIdx], checked: !items[itemIdx].checked };
      await updateShoppingList(userId.value, listId, { ...list, items });
      listsById.value = { ...listsById.value, [listId]: { ...list, items } };
    };

    const removeList = async (listId) => {
      if (!userId.value || !listId) return;
      try {
        await deleteShoppingList(userId.value, listId);
        const updatedLists = { ...listsById.value };
        delete updatedLists[listId];
        listsById.value = updatedLists;
        if (selectedListId.value === listId) selectedListId.value = null;
      } catch (e) {
        errorMessage.value = "Erreur lors de la suppression de la liste.";
      }
    };

    const printList = (list) => {
      if (!list) return;
      const win = window.open("", "_blank");
      if (!win) return;
      const safeListName = escapeHtml(list.name);
      const items = (list.items || [])
        .map(
          (item) => `
          <li class="item">
            <span class="check"></span>
            <span class="name">${escapeHtml(item.name)}</span>
            <span class="qty">x${escapeHtml(item.quantity)}</span>
          </li>
        `
        )
        .join("");
      const dateLabel = new Date().toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      win.document.write(`
        <html>
          <head>
            <title>${safeListName}</title>
            <style>
              * { box-sizing: border-box; }
              body {
                font-family: "Segoe UI", Arial, sans-serif;
                padding: 32px;
                color: #1f1f1f;
                background: #ffffff;
              }
              .page {
                max-width: 720px;
                margin: 0 auto;
                border: 1px solid #e6e6e6;
                border-radius: 16px;
                padding: 28px 28px 24px;
                box-shadow: 0 18px 40px rgba(0,0,0,0.08);
              }
              .header {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                margin-bottom: 18px;
              }
              .title {
                font-size: 1.6rem;
                font-weight: 700;
                margin: 0;
              }
              .date {
                font-size: 0.85rem;
                color: #6b6b6b;
              }
              .subtitle {
                margin: 0 0 16px;
                color: #6b6b6b;
                font-size: 0.95rem;
              }
              ul {
                list-style: none;
                padding: 0;
                margin: 0;
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                gap: 12px;
              }
              .item {
                display: grid;
                grid-template-columns: 18px 1fr auto;
                align-items: center;
                gap: 10px;
                padding: 10px 12px;
                border-radius: 12px;
                border: 1px solid #efefef;
                background: #fafafa;
              }
              .check {
                width: 16px;
                height: 16px;
                border-radius: 4px;
                border: 2px solid #b8b8b8;
                display: inline-block;
              }
              .name {
                font-weight: 600;
              }
              .qty {
                font-size: 0.85rem;
                color: #4a4a4a;
                background: #e9f4e3;
                padding: 2px 8px;
                border-radius: 999px;
                border: 1px solid #d7ebcc;
              }
              .footer {
                margin-top: 18px;
                font-size: 0.75rem;
                color: #8a8a8a;
                text-align: right;
              }
              @media print {
                body { padding: 0; }
                .page { box-shadow: none; border: none; }
              }
            .generateMenuBtn { display: flex; justify-content: flex-end; }
.panelItem { cursor: pointer; display: flex; align-items: center; gap: 8px; transition: opacity 0.2s; }
.panelItem--checked { opacity: 0.45; text-decoration: line-through; }
.panelItem__check { color: #7553FC; flex-shrink: 0; }
</style>
          </head>
          <body>
            <div class="page">
              <div class="header">
                <h1 class="title">${safeListName}</h1>
                <div class="date">${dateLabel}</div>
              </div>
              <p class="subtitle">${(list.items || []).length} article(s)</p>
              <ul>${items || "<li class='item'><span class='name'>Aucun article</span></li>"}</ul>
              <div class="footer">MealMaster • Liste de courses</div>
            </div>
          </body>
        </html>
      `);
      win.document.close();
      win.focus();
      win.print();
    };

    watch(
      userId,
      (value) => {
        if (value) loadLists();
      },
      { immediate: true }
    );

    return {
      authStore,
      errorMessage,
      shoppingSvg,
      showDialog,
      products,
      currentProduct,
      isEditing,
      editingIndex,
      addProduct,
      editProduct,
      updateProduct,
      removeProduct,
      listName,
      shoppingListArray,
      selectedListId,
      selectedList,
      hasLists,
      isEditingList,
      createShoppingList,
      openCreateModal,
      openEditModal,
      selectList,
      removeList,
      printList,
      formatDate,
      isGeneratingFromMenu,
      generateShoppingListFromMenu,
      toggleItemChecked,
    };
  },
};
</script>

<style scoped>
.rowCustom {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.pageHeader {
  align-items: center;
  gap: 16px;
}

.emptyStateCard {
  padding: 18px;
  border-radius: 26px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  background: linear-gradient(
    160deg,
    rgba(var(--v-theme-secondary), 0.95),
    rgba(var(--v-theme-secondary), 0.7)
  );
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.18);
}

.shoppingLayout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 24px;
  align-items: start;
}

.listColumn {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.listHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  border-radius: 20px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  background: rgba(var(--v-theme-secondary), 0.75);
  box-shadow: 0 12px 22px rgba(0, 0, 0, 0.16);
}

.listTitle {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
}

.listSubtitle {
  margin: 2px 0 0;
  font-size: 0.9rem;
  color: rgba(var(--v-theme-text), 0.65);
}

.listGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.listCard {
  text-align: left;
  width: 100%;
  appearance: none;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 18px;
  border: 1px solid rgba(var(--v-theme-text), 0.1);
  background: rgba(var(--v-theme-secondary), 0.85);
  padding: 14px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  color: inherit;
}

.listCard:focus-visible {
  outline: 2px solid rgba(var(--v-theme-accent), 0.6);
  outline-offset: 2px;
}

.listCard:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.18);
}

.listCard.active {
  border-color: rgba(var(--v-theme-accent), 0.6);
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.22);
}

.listCardHeader {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.listCardTitle {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
}

.listCardMeta {
  margin: 4px 0 0;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-text), 0.6);
}

.listCardDate {
  font-size: 0.74rem;
  color: rgba(var(--v-theme-text), 0.5);
}

.listPreview {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.listPreviewRow {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.85rem;
  padding: 6px 8px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.5);
}

.listPreviewRow span:first-child {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.listPreviewQty {
  color: rgba(var(--v-theme-text), 0.65);
  white-space: nowrap;
  flex: 0 0 auto;
}

.listPreviewMore {
  font-size: 0.78rem;
  color: rgba(var(--v-theme-text), 0.55);
}

.detailPanel {
  position: sticky;
  top: 18px;
  height: calc(100vh - 140px);
  border-radius: 22px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  background: rgba(var(--v-theme-secondary), 0.85);
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panelHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(var(--v-theme-text), 0.08);
  background: rgba(var(--v-theme-primary), 0.8);
}

.panelTitle {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.panelMeta {
  margin: 4px 0 0;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-text), 0.6);
}

.panelActions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.panelBody {
  flex: 1 1 auto;
  overflow: hidden;
  padding: 12px 14px 16px;
}

.panelItems {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  max-height: 100%;
  padding-right: 4px;
}

.panelItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(var(--v-theme-primary), 0.5);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
}

.panelItemName {
  font-weight: 600;
}

.panelItemQty {
  font-size: 0.82rem;
  color: rgba(var(--v-theme-text), 0.6);
}

.panelEmpty {
  padding: 14px;
  border-radius: 14px;
  border: 1px dashed rgba(var(--v-theme-text), 0.2);
  color: rgba(var(--v-theme-text), 0.6);
  text-align: center;
}

.shoppingModal {
  border-radius: 28px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  background: linear-gradient(
    160deg,
    rgba(var(--v-theme-primary), 0.96),
    rgba(var(--v-theme-primary), 0.9)
  );
  color: rgb(var(--v-theme-text));
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
  max-height: min(86vh, 760px);
  display: flex;
  flex-direction: column;
}

.modalHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 10px;
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-secondary), 0.65),
    rgba(var(--v-theme-secondary), 0.25)
  );
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
}

.modalTitle {
  font-size: 1.2rem;
  font-weight: 700;
}

.modalSubtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-text), 0.65);
}

.modalClose {
  margin-top: -6px;
  border-radius: 999px;
}

.modalBody {
  padding: 16px 24px 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
}

.listNameField {
  margin-bottom: 12px;
}

.productList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 0 4px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
}

.emptyProducts {
  padding: 14px 12px;
  border-radius: 12px;
  border: 1px dashed rgba(var(--v-theme-text), 0.18);
  color: rgba(var(--v-theme-text), 0.6);
  text-align: center;
}

.productRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  background: rgba(var(--v-theme-secondary), 0.8);
}

.productMeta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.productName {
  font-weight: 600;
}

.productQty {
  font-size: 0.82rem;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(var(--v-theme-text), 0.16);
  color: rgba(var(--v-theme-text), 0.7);
}

.productActions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.addRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px auto;
  gap: 10px;
  align-items: center;
  margin-top: 14px;
  position: sticky;
  bottom: 0;
  padding: 12px 0 6px;
  backdrop-filter: blur(6px);
}

.productField {
  min-width: 0;
}

.qtyField {
  max-width: 120px;
}

.addProductBtn {
  height: 44px;
  text-transform: none;
  font-weight: 600;
  border-radius: 999px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
}

.modalActions {
  padding: 6px 20px 20px;
  position: sticky;
  bottom: 0;
}

.primaryBtn {
  border-radius: 999px;
  font-weight: 600;
  padding: 0 20px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.2);
}

.ghostBtn {
  border-radius: 999px;
  font-weight: 600;
}

.shoppingModal :deep(.v-field) {
  border-radius: 14px;
  background: rgba(var(--v-theme-primary), 0.92) !important;
}

.shoppingModal :deep(.v-field__field),
.shoppingModal :deep(.v-input__control),
.shoppingModal :deep(.v-field__overlay) {
  background: rgba(var(--v-theme-primary), 0.92) !important;
}

.shoppingModal :deep(.v-field__outline) {
  color: rgba(var(--v-theme-text), 0.35);
}

.shoppingModal :deep(.v-field__input) {
  color: rgb(var(--v-theme-text));
}

.shoppingModal :deep(.v-label) {
  color: rgba(var(--v-theme-text), 0.7);
}

.shoppingModal :deep(.v-field__overlay) {
  opacity: 0.08;
}

.shoppingModal :deep(.v-field__prepend-inner),
.shoppingModal :deep(.v-field__append-inner) {
  color: rgba(var(--v-theme-text), 0.7);
}

@media (max-width: 720px) {
  .addRow {
    grid-template-columns: 1fr;
  }
  .qtyField {
    max-width: none;
  }
}

@media (max-width: 1100px) {
  .shoppingLayout {
    grid-template-columns: 1fr;
  }
  .detailPanel {
    position: relative;
    height: auto;
  }
}
</style>

<!-- Premier version liste 

 <v-dialog v-model="showDialog" max-width="600px">
        <v-card>
            <v-card-title>Créer une nouvelle liste</v-card-title>

            <v-card-text>
            <v-text-field v-model="listName" label="Nom de la liste" />

            <div v-for="(item, index) in products" :key="index" class="d-flex align-center mb-2">
                <v-text-field
                v-model="item.name"
                label="Produit"
                class="mr-2"
                dense
                hide-details
                />
                <v-text-field
                v-model.number="item.quantity"
                label="Qté"
                type="number"
                min="1"
                style="width: 80px"
                dense
                hide-details
                />
            </div>

            <v-btn icon @click="addProduct">
                <v-icon>mdi-plus</v-icon>
            </v-btn>
            </v-card-text>

            <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="submitList">Créer</v-btn>
            <v-btn text @click="showDialog = false">Annuler</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog> -->
